/**
 * Logs call activity into Salesforce using Open CTI
 * This function is SAFE to call even if Salesforce (sforce) is not available
 */
import {
  externalUpdateCallHistory,
  externalGetCallHistoryItem,
} from "../context/CallHistoryContext";
import { checkSession } from "../../common/session";
import {
  parseCallCenterSettings,
  getWrapupConfiguration,
  getRecordTypeIdForCallTask,
} from "./callCenterSettings";

let cachedUserCountryCode = null;
let actEnabledWhatObjIdPrefixList = [];

const getDefaultWrapupResultForGroup = (callResultGroup) => {
  const wrapupGroups = Array.isArray(getWrapupConfiguration())
    ? getWrapupConfiguration().filter(Boolean)
    : [];

  const selectedGroup = callResultGroup
    ? wrapupGroups.find((group) => group?.name === callResultGroup)
    : wrapupGroups[0];

  const configuredDefault = (selectedGroup?.defaultValue || "").trim();
  if (configuredDefault && selectedGroup?.values?.includes(configuredDefault)) {
    return configuredDefault;
  }

  return "";
};

export const loadCallCenterSettings = ({ onLoaded } = {}) => {
  return new Promise((resolve) => {
    try {
      if (typeof sforce === "undefined" || !sforce.opencti) {
        console.warn("Salesforce not available, skipping call center settings");
        resolve();
        return;
      }

      sforce.opencti.getCallCenterSettings({
        callback: (response) => {
          console.log("Inside loaded Call Center Settings ", response);
          if (!response?.success || !response?.returnValue) {
            console.error("Error Loading Call Center Settings:", response.errors);
            resolve();
            return;
          }

          console.log(
            "getCallCenterSettings API method call executed successfully! returnValue:",
            response.returnValue,
          );
          parseCallCenterSettings(response.returnValue);

          if (onLoaded) {
            onLoaded();
          }
          resolve();
        },
      });
    } catch (e) {
      console.error("getCallCenterSettings : Failed : ", e);
      resolve();
    }
  });
};

export const updateSalesforceActivity = ({
  whoId,
  whoName,
  whoType,
  whatId,
  whatName,
  whatType,
  callObject,
  activityId,
  callResult,
  callResultGroup,
  notes,
  subject,
  direction,
  isAnswered,
  number,
  name,
  showEditSubject,
  setErrorMessage,
}) => {
  try {
    if (typeof sforce === "undefined" || !sforce.opencti || !sforce.opencti.saveLog) {
      return;
    }

    let callResultTmp = callResult || "";
    if (callResultTmp && (callResultTmp == "none" || callResultTmp == "--None--")) {
      callResultTmp = "";
    }

    const counterpart = number || name || "";

    let callType = !isAnswered
      ? direction === "Inbound"
        ? "Missed"
        : "Unanswered"
      : direction || "Call";
    if (!callType.includes("Call")) {
      callType += " Call";
    }
    const subjectNew =
      subject ||
      callType +
        (counterpart
          ? direction === "Inbound"
            ? ` from ${counterpart}`
            : ` to ${counterpart}`
          : "");

    let isSafe = false;
    let task = {
      Id: activityId,
      entityApiName: "Task",
    };

    const histItem = externalGetCallHistoryItem
      ? externalGetCallHistoryItem(callObject + "-" + activityId)
      : null;
    //Update Subject only if it changed from the one saved on call end
    if (showEditSubject) {
      const savedSubject = histItem?.subject || "";
      if ((subjectNew || "") !== savedSubject) {
        task.Subject = subjectNew || "";
        isSafe = true;
      }
    }

    //Update below fields only if they changed from the ones saved in call history
    const savedDescription = histItem?.notes || "";
    if ((notes || "") !== savedDescription) {
      task.Description = notes || "";
      isSafe = true;
    }

    const savedWhoId = histItem?.whoId || "";
    if ((whoId || "") !== savedWhoId) {
      task.WhoId = whoId || "";
      isSafe = true;
    }

    const savedWhatId = histItem?.whatId || "";
    if ((whatId || "") !== savedWhatId) {
      task.WhatId = whatId || "";
      isSafe = true;
    }

    const savedCallResult = histItem?.callResult || "";
    if (callResultTmp !== savedCallResult) {
      task.CallDisposition = callResultTmp;
      isSafe = true;
    }

    if (callResultGroup && callResultTmp) {
      const savedCallResultGroup = histItem?.callResultGroup || "";
      if (callResultGroup !== savedCallResultGroup) {
        task.unrc__CallResultGroup__c = callResultGroup;
        isSafe = true;
      }
    }

    //Nothing changed, no need to call saveLog with just Id & entityApiName
    if (!isSafe) {
      console.log("update : No change in task fields, skipping saveLog");
      return;
    }

    const saveLogCallback = (res) => {
      if (res && res.success && res.returnValue) {
        console.log("update : Salesforce saveLog result success");
        let callHistObj = {
          id: callObject + "-" + activityId,
          name,
          whoName: whoName || "",
          whatName: whatName || "",
          whoType: whoType || "",
          whatType: whatType || "",
        };
        //only overwrite history fields when they were actually updated on task
        if (task.Subject !== undefined) {
          callHistObj.subject = task.Subject;
        }
        if (task.Description !== undefined) {
          callHistObj.notes = task.Description;
        }
        if (task.WhoId !== undefined) {
          callHistObj.whoId = task.WhoId;
        }
        if (task.WhatId !== undefined) {
          callHistObj.whatId = task.WhatId;
        }
        if (task.CallDisposition !== undefined) {
          callHistObj.callResult = task.CallDisposition;
        }
        if (task.unrc__CallResultGroup__c !== undefined) {
          callHistObj.callResultGroup = task.unrc__CallResultGroup__c;
        }

        if (externalUpdateCallHistory) {
          externalUpdateCallHistory(callHistObj);
        }
      } else {
        console.error("update : Salesforce saveLog result failed:", res);
        saveLogErrHandler(res, setErrorMessage, whatName);
      }
    };

    sforce.opencti.saveLog({ value: task, callback: saveLogCallback });
  } catch (error) {
    console.warn("Failed to update Salesforce activity log:", error);
  }
};

export const logSalesforceActivity = async ({
  direction,
  number,
  name,
  matchedRecordId,
  whoId,
  whoName,
  whoType,
  whatId,
  whatName,
  whatType,
  durationSec,
  callResult,
  callResultGroup,
  upsertCallMap,
  hvsCallId,
  callId,
  notes,
  subject,
  //lastEntityIdRef,
  startedAt,
  startedAtDateTimeStr,
  startedAtUtc,
  endedAtUtc,
  queueName, //Agent 1/2
  holdDurationSec,
  ringDurationSec,
  callFromPhone, //Inbound = Customer phonenumber
  callFromUpn, //Inbound = Customer UPN = Other agent's UPN = internal call / transfer leg
  callToPhone, //Inbound = Agent's number
  callToUPN,
  callHandlerId,
  callerGraphUserId,
  transferredBy, //Agent 2's end, name of agent 1
  transferFromUpn, // Agent 2's end - upn of agent 1
  forwardToPhone, //Agent1
  forwardToUPN, //Agent 1
  setErrorMessage,
  isRetry,
}) => {
  try {
    // Ensure Salesforce Open CTI is available
    const { authenticated, user } = await checkSession();
    const rawUpn = authenticated && user ? user.upn : null;
    const rawUserObjectId = authenticated && user ? user.userObjectId : null;

    if (typeof sforce === "undefined" || !sforce.opencti || !sforce.opencti.saveLog) {
      return;
    }

    const counterpart = number || name || "";

    // Salesforce Task object payload
    const callAnsDurInSec = typeof durationSec === "number" ? durationSec : 0;

    let callType =
      callAnsDurInSec === 0
        ? direction === "Inbound"
          ? "Missed"
          : "Unanswered"
        : direction || "Call";
    if (!callType.includes("Call")) {
      callType += " Call";
    }

    const ringDurationInSec = typeof ringDurationSec === "number" ? ringDurationSec : 0;
    const totalHoldDurationInSec = typeof holdDurationSec === "number" ? holdDurationSec : 0;

    //const subject = callType + (counterpart ? direction === "Inbound" ? ` from ${counterpart}` : ` to ${counterpart}` : "");

    //will now work for unanswered calls only, as do not save missed call, and for answered call, checks are already on disconnected state to createcallSubject
    const subjectNew =
      subject ||
      callType +
        (counterpart
          ? direction === "Inbound"
            ? ` from ${counterpart}`
            : ` to ${counterpart}`
          : "");

    let callResultTmp = callResult || "";
    if (!callResultTmp && callAnsDurInSec === 0 && direction === "Outbound") {
      callResultTmp = getDefaultWrapupResultForGroup(callResultGroup);
    } else if (callResultTmp && (callResultTmp == "none" || callResultTmp == "--None--")) {
      callResultTmp = "";
    }
    const task = {
      entityApiName: "Task",
      Subject: subjectNew,
      Status: callAnsDurInSec === 0 && direction === "Inbound" ? "Missed" : "Completed",
      // do not pass "Transfer" -> error = INVALID_OR_NULL_FOR_RESTRICTED_PICKLIST: Call Type: bad value for restricted picklist field: Transfer
      CallType: direction || "Inbound ", //Unknown replaced with Inbound else task save fails due to bad picklist value
      CallObject: callId || "",
      CallDisposition: callResultTmp,
      CallDurationInSeconds: callAnsDurInSec,
      unrc__CallToPhone__c: callToPhone || "", //Inbound = Agent's number
      unrc__CallHandlerUPN__c: rawUpn || "",
      unrc__CallHandlerPhone__c: callHandlerId || "",
      unrc__CallHandlerUserId__c: rawUserObjectId || "",
      unrc__CallFromPhone__c: callFromPhone || "", //Inbound = Customer phonenumber
      unrc__CallFromUPN__c: callFromUpn || "", //Inbound = Customer UPN = Other agent's UPN = internal call / transfer leg
      Description: notes || "",
    };

    if (callResultGroup && callResultTmp) {
      task.unrc__CallResultGroup__c = callResultGroup;
    }

    if (callToUPN) {
      task.unrc__CallToUPN__c = callToUPN;
    }

    const taskRecordType = getRecordTypeIdForCallTask();
    if (taskRecordType) {
      task.RecordTypeId = taskRecordType;
    }

    task.WhoId = whoId || "";
    task.WhatId = whatId || "";

    /*const whoIdPrefixes = ["003", "00Q"];
    //const whatIdPrefixes = ["001", "006", "500", "701"]; //future - use with assignment logic
    const whatIdPrefixesDontSave = ["00v"]; //future - use with assignment logic
    const donotAssignPrefixesToTask = ["00T", "00U", "02s", "01Z", "00O", "00l"]; //02s = EmailMessage, 01Z -> Dashboard, 00O -> Report, 00l -> Folder

    // Attach related Salesforce record if available
    let whoName = '', whatName = '';
    let recordIdToAssign = matchedRecordId || lastEntityIdRef?.current;
    if (recordIdToAssign) {
      if (whoIdPrefixes.includes(recordIdToAssign.substring(0, 3))) {
        task.WhoId = recordIdToAssign;
      } else {
        task.WhatId = recordIdToAssign;
      }
    }
	
	  if (task?.WhoId && donotAssignPrefixesToTask.includes(task.WhoId.substring(0, 3))) {
      task.WhoId = '';
    }
	
    if (task?.WhatId && (donotAssignPrefixesToTask.includes(task.WhatId.substring(0, 3)) 
        || whatIdPrefixesDontSave.includes(task.WhatId.substring(0, 3)))) {
      task.WhatId = '';
    }
    if(name) {
      if(task?.WhoId) {
        whoName = name || "";
      } else {
        whatName = name || "";
      }
    }*/

    let dueDate;
    if (startedAtDateTimeStr) {
      dueDate = startedAtDateTimeStr.substr(0, 10);
    } else {
      let currentDate = new Date();
      let currentDay = currentDate.getDate();
      let currentMonth = currentDate.getMonth() + 1;
      let currentYear = currentDate.getFullYear();
      dueDate = currentYear + "-" + currentMonth + "-" + currentDay;
    }
    task.ActivityDate = dueDate;

    task.TaskSubtype = "Call";
    task.Type = "Call";
    task.unrc__CalledQueue__c = queueName || "";
    task.unrc__CallStart__c = startedAtUtc;
    task.unrc__CallEnd__c = endedAtUtc;
    task.unrc__HoldDuration__c = totalHoldDurationInSec;

    if (forwardToPhone) {
      task.unrc__TransferToPhone__c = forwardToPhone;
    } else if (forwardToUPN) {
      task.unrc__TransferToUPN__c = forwardToUPN;
    }

    if (transferFromUpn) {
      task.unrc__TransferFromUPN__c = transferFromUpn || "";
    }

    const saveLogCallback = (res) => {
      if (res && res.success && res.returnValue) {
        if (res?.returnValue?.recordId && task.CallDurationInSeconds > 0 && !isRetry) {
          upsertCallMap(task.CallObject, {
            activityId: res.returnValue.recordId,
          });
        }
        if (
          hvsCallId != null &&
          hvsCallId != undefined &&
          hvsCallId != "" &&
          sforce &&
          sforce.opencti &&
          sforce.opencti.hvs
        ) {
          sforce.opencti.hvs.completeWork({
            workId: hvsCallId, // Id sent via onWorkStart
            attributes: {
              disposition: callResultTmp, // Disposition value like 'Completed'
              taskId: res?.returnValue?.recordId || "", // ID of task created
              wasConnected: callAnsDurInSec > 0 ? true : false, // Whether the call successfully connected
            },
            callback: function () {
              /* perform cleanup here */
            },
          });
        } else {
          console.log("SaveCall : hvsCallId is empty in lightning");
        }
        let callHistObj = {
          id: task.CallObject + "-" + (res?.returnValue?.recordId || ""),
          hvsCallId,
          startedAt, //required for sorting
          startedAtDateTimeStr,
          callerGraphUserId,
          activityId: res?.returnValue?.recordId || "",
          ansDurInSec: task.CallDurationInSeconds,
          ringDurationInSec,
          totalHoldDurationInSec,
          callType: task.CallType,
          whoId: task.WhoId,
          whatId: task.WhatId,
          whoName: whoName || "",
          whatName: whatName || "",
          whoType: whoType || "",
          whatType: whatType || "",
          dialedUpn: callToUPN || "",
          callerUpn: callFromUpn || "", //Inbound = Customer UPN = Other agent's UPN = internal call / transfer leg
          callerNumber: task.CallType == "Inbound" ? number : "", //inbound = call comes from, outbound = call made to
          dialedNumber: task.CallType != "Inbound" ? number : "",
          name,
          transferFromUpn,
          subject: task.Subject,
          queueName: queueName || "",
          transferredBy: transferredBy || "", //Agent 2
          notes: task.Description,
          callResult: task.CallDisposition || "",
          callResultGroup: task.unrc__CallResultGroup__c || "",
        };

        if (externalUpdateCallHistory) {
          externalUpdateCallHistory(callHistObj);
        }
      } else {
        console.error("Salesforce saveLog result failed:", res);
        saveLogErrHandler(res, setErrorMessage, whatName);
      }
    };

    // Save log to Salesforce
    sforce.opencti.saveLog({ value: task, callback: saveLogCallback });
  } catch (error) {
    console.warn("Failed to save Salesforce activity log:", error);
  }
};

const saveLogErrHandler = (res, setErrorMessage, whatName) => {
  let errFound = false;
  let fieldErrors = res.errors?.[0]?.details?.fieldErrors;
  let pageErrors = res.errors?.[0]?.details?.pageErrors;
  /*let targetKeysForErrMsg = [
    "WhatId",
    "unrc__CallResultGroup__c"
  ];*/
  if (fieldErrors) {
    for (const key of Object.keys(fieldErrors)) {
      if (errFound) break;
      const errorsForKey = fieldErrors[key];
      if (!errorsForKey || !Array.isArray(errorsForKey)) continue;

      for (const error of errorsForKey) {
        if (errFound) break;
        const status = error?.statusCode ?? "UNKNOWN_ERROR";
        const msg = error?.message ?? "";

        if (
          status === "STRING_TOO_LONG" &&
          msg &&
          msg.startsWith("Call Result Group: ") &&
          msg.endsWith("(max length=40)")
        ) {
          errFound = true;
          setErrorMessage(
            "Call Wrap-Up Group has exceeded character limit (max length=40). Contact sf admin to update it or select another Group.",
          );
        } else if (
          status === "FIELD_INTEGRITY_EXCEPTION" &&
          msg &&
          msg.startsWith("Related To ID: id value of incorrect type")
        ) {
          errFound = true;
          setErrorMessage(
            "Save failed - '" +
              whatName +
              "' cannot be assigned as Related To ID. Contact SF admin to 'Allow Activities' on target object or remove/replace the assignment.",
          );
        } else {
          errFound = true;
          if (msg) {
            setErrorMessage(`${status}: ${msg}`);
          } else {
            setErrorMessage(status);
          }
        }
      }
    }
  } else if (pageErrors) {
    for (const key of Object.keys(pageErrors)) {
      if (errFound) break;
      const errorsForKey = pageErrors[key];
      if (!errorsForKey || !Array.isArray(errorsForKey)) continue;

      for (const error of errorsForKey) {
        if (errFound) break;
        const status = error?.statusCode ?? "UNKNOWN_ERROR";
        const msg = error?.message ?? "";
        errFound = true;
        if (msg) {
          setErrorMessage(`${status}: ${msg}`);
        } else {
          setErrorMessage(status);
        }
      }
    }
  }
  if (!errFound) {
    setErrorMessage(
      "Failed to save task - Please check browser console for detailed error message",
    );
  }
};

//sets label of panel (at top)
export const setSoftphonePanelLabel = ({ label }) => {
  try {
    if (typeof sforce === "undefined" || !sforce.opencti || !label) {
      return;
    }
    sforce.opencti.setSoftphonePanelLabel({
      label,
      callback: async (response) => {
        console.log("Inside setSoftphonePanelLabelCallback " + response);
        if (response.success) {
          console.log("API method call executed successfully! returnValue:", response.returnValue);
        } else {
          console.log("Something went wrong! Errors:", response.errors);
        }
      },
    });
  } catch (e) {
    console.warn("setSoftphonePanelLabel : Failed : ", e);
  }
};

//sets label in util (at bottom)
export const setSoftphoneItemLabel = ({ label }) => {
  try {
    if (typeof sforce === "undefined" || !sforce.opencti || !label) {
      return;
    }
    sforce.opencti.setSoftphoneItemLabel({
      label,
      callback: async (response) => {
        console.log("Inside setSoftphoneItemLabelCallback " + response);
        if (response.success) {
          console.log("API method call executed successfully! returnValue:", response.returnValue);
        } else {
          console.log("Something went wrong! Errors:", response.errors);
        }
      },
    });
  } catch (e) {
    console.warn("setSoftphoneItemLabel : Failed : ", e);
  }
};

export const setSoftphonePanelHeight = ({ heightPX }) => {
  try {
    if (typeof sforce === "undefined" || !sforce.opencti || !heightPX) {
      return;
    }
    sforce.opencti.setSoftphonePanelHeight({
      heightPX,
    });
  } catch (e) {
    console.warn("setSoftphonePanelHeight : Failed : ", e);
  }
};

export const isSoftphonePanelVisible = ({ onSuccess }) => {
  try {
    if (typeof sforce !== "undefined" && sforce.opencti) {
      sforce.opencti.isSoftphonePanelVisible({
        callback: (response) => {
          let isVisible = false;
          if (response?.success && response?.returnValue?.visible) {
            isVisible = true;
          }
          /*,onSuccess: ({ recordId, record }) => {},*/
          // pass normalized result back to caller
          onSuccess?.({
            isVisible,
          });
        },
      });
    }
  } catch (e) {
    console.warn("isSoftphonePanelVisible - Failed : ", e);
  }
};

export const setSoftphonePanelVisibility = (visible) => {
  try {
    if (typeof sforce !== "undefined" && sforce.opencti) {
      isSoftphonePanelVisible({
        onSuccess: ({ isVisible }) => {
          if (isVisible != visible) {
            sforce.opencti.setSoftphonePanelVisibility({ visible });
          }
        },
      });
    }
  } catch (e) {
    console.warn("Failed to set softphone panel visibility:", e);
  }
};

export const setSoftphoneItemIcon = (key) => {
  try {
    if (typeof sforce !== "undefined" && sforce.opencti) {
      sforce.opencti.setSoftphoneItemIcon({ key });
    }
  } catch (e) {
    console.warn("Failed to set softphone item icon:", e);
  }
};

export const getListOfWhatIdObjWithActivityEnabled = () => {
  if (typeof sforce === "undefined" || !sforce.opencti) {
    return;
  }
  try {
    sforce.opencti.runApex({
      apexClass: "unrc.UnaricUnifyClientCtiController",
      methodName: "getListOfWhatIdObjWithActivityEnabled",
      callback: (result) => {
        if (result?.success && result?.returnValue?.runApex) {
          const wrapperJson = result.returnValue.runApex;
          actEnabledWhatObjIdPrefixList = wrapperJson ? JSON.parse(wrapperJson) : [];
          console.log("Parsed prefixes:", actEnabledWhatObjIdPrefixList);
        } else {
          console.error(
            "page - Error calling Apex getListOfWhatIdObjWithActivityEnabled:",
            result?.errors,
          );
          //e.x. - Could not find method: getListOfWhatIdObjWithActivityEnabled
        }
      },
    });
  } catch (e) {
    console.warn("getListOfWhatIdObjWithActivityEnabled : Failed : ", e);
  }
};

export const getWhatIdListOnTask = () => {
  return actEnabledWhatObjIdPrefixList;
};

export const screenPop = ({ type, params }) => {
  try {
    if (typeof sforce === "undefined" || !sforce.opencti || !type || !params) {
      return;
    }

    //type = SOBJECT/ URL/ OBJECTHOME/ LIST/ SEARCH/ NEW_RECORD_MODAL/ FLOW
    //edit task = URL , params : {url: url}
    sforce.opencti.screenPop({
      type: sforce.opencti.SCREENPOP_TYPE[type] || type,
      params,
    });
  } catch (e) {
    console.warn("screenPop : Failed : ", e);
  }
};

/**
 * Returns the matched record IDs from an OpenCTI searchAndScreenPop returnValue,
 * excluding the reserved SCREEN_POP_DATA key.
 */
export const extractMatchedRecordIds = (returnValue) => {
  if (!returnValue || typeof returnValue !== "object") return [];
  return Object.keys(returnValue).filter((key) => key !== "SCREEN_POP_DATA");
};

/**
 * Mutates popData in-place to append matched record ID(s) into the URL or
 * Flow args, depending on the pop type. Returns popData for convenience.
 */
export const enrichScreenPopData = (popData, matchedRecordIds) => {
  if (!popData) return popData;

  if (
    popData.type === sforce.opencti.SCREENPOP_TYPE.URL &&
    popData.params?.url.startsWith("/apex/")
  ) {
    const hasQuery = popData.params.url.includes("?");
    const separator = hasQuery ? "&" : "?";
    if (matchedRecordIds.length > 1) {
      popData.params.url += `${separator}recordIds=${matchedRecordIds.map((id) => encodeURIComponent(id)).join("%2C")}`;
    } else if (matchedRecordIds.length === 1) {
      popData.params.url += `${separator}recordId=${encodeURIComponent(matchedRecordIds[0])}`;
    }
  } else if (
    popData.type === sforce.opencti.SCREENPOP_TYPE.FLOW &&
    Array.isArray(popData.params?.flowArgs)
  ) {
    if (matchedRecordIds.length > 1) {
      popData.params.flowArgs.push({
        name: "recordIds",
        type: "String",
        value: matchedRecordIds.join(","),
      });
    } else if (matchedRecordIds.length === 1) {
      popData.params.flowArgs.push({
        name: "recordId",
        type: "String",
        value: matchedRecordIds[0],
      });
    }
  }

  return popData;
};

export const handleRecordAssignmentForSingleMatch = (
  recordId,
  val,
  onSuccess,
  matchedRecordIds,
  popData,
) => {
  const recordObj = val.returnValue[recordId] || {};

  let srchRes = {
    whoId: null,
    whoName: null,
    whoType: null,
    whatId: null,
    whatName: null,
    whatType: null,
  };
  if (recordId.substring(0, 3) == "003" || recordId.substring(0, 3) == "00Q") {
    srchRes.whoId = recordId;
    srchRes.whoName = recordObj.Name;
    srchRes.whoType = recordObj.RecordType;
  } else {
    srchRes.whatId = recordId;
    srchRes.whatName = recordObj.Name;
    srchRes.whatType = recordObj.RecordType;
  }
  if (
    sforce &&
    sforce.opencti &&
    (recordId.substring(0, 3) == "003" ||
      recordId.substring(0, 3) == "500" ||
      recordId.substring(0, 3) == "00T" ||
      recordId.substring(0, 3) == "00U" ||
      recordId.substring(0, 3) == "00v")
  ) {
    //00v = campaign member
    try {
      sforce.opencti.runApex({
        apexClass: "unrc.UnaricUnifyClientCtiController",
        methodName: "getRelatedRecordInfo",
        methodParams:
          "recordId=" +
          recordId +
          "&recordName=" +
          recordObj.Name +
          "&objectType=" +
          recordObj.RecordType,
        callback: (result) => {
          if (result?.success && result?.returnValue?.runApex) {
            const wrapperJson = result.returnValue.runApex;
            const wrapper = wrapperJson ? JSON.parse(wrapperJson) : null;

            if (wrapper && (wrapper.whoId || wrapper.whatId)) {
              onSuccess?.({
                recordId,
                record: wrapper,
                popData,
                matchedRecordIds,
              });
            } else {
              onSuccess?.({
                recordId,
                record: srchRes,
                popData,
                matchedRecordIds,
              });
            }
          } else {
            console.error("Error calling Apex getRelatedRecordInfo:", result?.errors);
            onSuccess?.({
              recordId,
              record: srchRes,
              popData,
              matchedRecordIds,
            });
          }
        },
      });
    } catch (ex) {
      console.warn("call to apex getRelatedRecordInfo Failed :", ex);
      onSuccess?.({ recordId, record: srchRes, popData, matchedRecordIds });
    }
  } else {
    onSuccess?.({ recordId, record: srchRes, popData, matchedRecordIds });
  }
};

/**
 * Processes the raw OpenCTI searchAndScreenPop callback value.
 * screenPopFn is injected so this function has no dependency on the sforce global.
 */
export const handleSearchAndScreenPopCallback = (
  val,
  { shouldPop, onSuccess, onNoMatch, screenPopFn },
) => {
  if (!val?.success || !val.returnValue) return;

  const matchedRecordIds = extractMatchedRecordIds(val.returnValue);
  const popData = val.returnValue.SCREEN_POP_DATA;

  if (popData) {
    enrichScreenPopData(popData, matchedRecordIds);
  }

  if (matchedRecordIds.length === 0) {
    //no match
    onNoMatch?.({ matchedRecordIds, popData });
  } else if (matchedRecordIds.length > 1) {
    //multiple match
    const singleAccountId = matchedRecordIds.filter((item) => item.startsWith("001"));
    if (singleAccountId.length === 1) {
      handleRecordAssignmentForSingleMatch(
        singleAccountId[0],
        val,
        onSuccess,
        matchedRecordIds,
        popData,
      );
    } else {
      onNoMatch?.({ matchedRecordIds, popData });
    }
  } else {
    //single match
    const donotAssignPrefixesToTask = ["00T", "00U", "02s", "01Z", "00O", "00l"]; //02s = EmailMessage, 01Z -> Dashboard, 00O -> Report, 00l -> Folder
    const actEnabledWhatIdPrefixes = getWhatIdListOnTask();
    const whoIdPrefixes = ["003", "00Q"];
    if (
      donotAssignPrefixesToTask.includes(matchedRecordIds[0].substring(0, 3)) ||
      (actEnabledWhatIdPrefixes &&
        !whoIdPrefixes.includes(matchedRecordIds[0].substring(0, 3)) &&
        !actEnabledWhatIdPrefixes.includes(matchedRecordIds[0].substring(0, 3)))
    ) {
      onNoMatch?.({ matchedRecordIds: [], popData });
    } else {
      handleRecordAssignmentForSingleMatch(
        matchedRecordIds[0],
        val,
        onSuccess,
        matchedRecordIds,
        popData,
      );
    }
  }

  if (shouldPop && popData) {
    screenPopFn?.(popData);
  }
};

export const searchAndScreenPop = ({
  searchParams,
  callType,
  popParams,
  shouldPop = true,
  onSuccess,
  onNoMatch,
}) => {
  try {
    if (typeof sforce === "undefined" || !sforce.opencti || !sforce.opencti.searchAndScreenPop) {
      onNoMatch?.({ matchedRecordIds: [] });
      return;
    }

    const defaultFieldValues = {};
    if (
      popParams?.number !== null &&
      popParams?.number !== undefined &&
      String(popParams.number).trim() !== ""
    ) {
      defaultFieldValues.Phone = String(popParams.number);
    }

    sforce.opencti.searchAndScreenPop({
      searchParams,
      callType,
      queryParams: generateVisualforceParams(popParams), //only relevant for Pop to Visualforce
      params: generateFlowParams(popParams),
      defaultFieldValues,
      deferred: true,
      callback: (val) =>
        handleSearchAndScreenPopCallback(val, {
          shouldPop,
          onSuccess,
          onNoMatch,
          screenPopFn: (data) => sforce.opencti.screenPop(data),
        }),
    });
  } catch (e) {
    console.warn("Failed to search and screen pop:", e);
    onNoMatch?.({ matchedRecordIds: [] });
  }
};

export const generateVisualforceParams = (popParams) => {
  if (!popParams || typeof popParams !== "object" || Array.isArray(popParams)) {
    return "";
  }

  return Object.entries(popParams)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
};

export const generateFlowParams = (popParams) => {
  if (!popParams || typeof popParams !== "object" || Array.isArray(popParams)) {
    return { FLOW: { flowArgs: [] } };
  }

  const flowArgs = Object.entries(popParams)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => ({
      name: key,
      type: "String",
      value: String(value),
    }));

  return { FLOW: { flowArgs } };
};

export const searchAndScreenPopOnly = ({ searchParams, callType, queryParams, onSuccess }) => {
  try {
    if (typeof sforce === "undefined" || !sforce.opencti || !sforce.opencti.searchAndScreenPop) {
      return;
    }

    sforce.opencti.searchAndScreenPop({
      searchParams,
      callType,
      queryParams,
      deferred: false,
      callback: (val) => {
        if (!val?.success || !val.returnValue) return;

        const recordId = Object.keys(val.returnValue)[0];
        if (!recordId) return;

        const recordObj = val.returnValue[recordId] || {};

        // pass normalized result back to caller
        onSuccess?.({
          recordId,
          record: recordObj,
          raw: val,
        });
      },
    });
  } catch (e) {
    console.warn("Failed to search and screen pop:", e);
  }
};

export const disableClickToDial = () => {
  try {
    if (typeof sforce !== "undefined" && sforce.opencti) {
      sforce.opencti.disableClickToDial({
        callback: (response) => {
          if (response?.success) {
            console.log(
              "disableClickToDial() API method call executed successfully! returnValue:",
              response?.returnValue,
            );
          } else {
            console.error("Something went wrong disableClickToDial! Errors:", response?.errors);
          }
        },
      });
    }
  } catch (e) {
    console.warn("Failed to disable click to dial:", e);
  }
};

export const enableClickToDial = () => {
  try {
    if (typeof sforce !== "undefined" && sforce.opencti) {
      sforce.opencti.enableClickToDial();
    }
  } catch (e) {
    console.warn("Failed to enable click to dial:", e);
  }
};

export const onClickToDial = (listener) => {
  try {
    if (typeof sforce !== "undefined" && sforce.opencti) {
      sforce.opencti.onClickToDial(listener);
    }
  } catch (e) {
    console.warn("Failed to set click to dial listener:", e);
  }
};

export const extractCountryCodeFromLocale = (locale) => {
  if (!locale) return null;

  // Salesforce locale values are underscore-separated; normalize dashes for browser locale fallback.
  const normalized = String(locale).trim().toUpperCase().replace(/-/g, "_");
  const match = normalized.match(/_([A-Z]{2})(?:_|$)/);
  return match ? match[1] : null;
};

const getBrowserLocaleCountryCode = () => {
  if (typeof navigator === "undefined") return null;

  const locales = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
    navigator.userLanguage,
  ].filter(Boolean);

  for (const locale of locales) {
    const countryCode = extractCountryCodeFromLocale(locale);
    if (countryCode) return countryCode;
  }

  return null;
};

export const getStoredUserCountryCode = () => {
  if (cachedUserCountryCode) {
    return cachedUserCountryCode;
  }

  const browserCountryCode = getBrowserLocaleCountryCode();
  if (browserCountryCode) {
    cachedUserCountryCode = browserCountryCode;
    return cachedUserCountryCode;
  }

  return null;
};

export const fetchAndStoreUserLocale = () => {
  try {
    if (typeof sforce === "undefined" || !sforce.opencti || !sforce.opencti.runApex) {
      cachedUserCountryCode = getBrowserLocaleCountryCode();
      return;
    }

    sforce.opencti.runApex({
      apexClass: "System.UserInfo",
      methodName: "getLocale",
      methodParams: "",
      callback: (result) => {
        if (result?.success) {
          const locale = result?.returnValue?.runApex;
          const countryCode = extractCountryCodeFromLocale(locale);
          cachedUserCountryCode = countryCode || getBrowserLocaleCountryCode();
        } else {
          cachedUserCountryCode = getBrowserLocaleCountryCode();
          console.warn("UserInfo.getLocale failed:", result?.errors);
        }
      },
    });
  } catch (error) {
    cachedUserCountryCode = getBrowserLocaleCountryCode();
    console.warn("Failed to fetch user locale from Salesforce:", error);
  }
};
